import { GroupBooking, VenueRequestFile, VenueRequestStatus } from '@go-shared/types/groupBooking';
import { BareSimpleSailingDayBundle } from '@go-shared/types/sailing';
import { VenueConfiguration, VenueTemplate } from '@go-shared/types/venueTemplate';
import React, { useState } from 'react';

import {
  BubbleAlert,
  BubbleBadge,
  BubbleButton,
  BubbleFormGroup,
  BubbleInput,
  BubbleSelect,
  BubbleTypeahead,
} from '../BubbleUI';
import BubbleFileM from '../BubbleUI/BubbleFileM';
import { FormGenericContainer } from '../FormGenericContainer';
import { BookingForm, GroupForm } from './Workflows';
import FormStatusFooter from './FormStatusFooter';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { hashStringToColor } from '@go-shared/lib/utils';
import { hasVenueHoursConflict } from '@go-shared/lib/plan';
import { DEFAULT_DAY_START } from '@go-shared/lib/property';
import { timeToMinutes } from '@go-shared/lib/time';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { canBookRequestsBypassingTimeRestrictions } from '@go-shared/lib/permissions';
import TinyEditor from '../TinyEditor';

interface EnterBookingDetailsPageProps {
  bookingForm: BookingForm;
  changeBookingForm: <T extends keyof BookingForm>(key: T, val: BookingForm[T]) => void;
  clearBookingForm?: () => void;
  venueConfigOpts: VenueConfiguration[];
  title?: string;
  pageIndex?: number;
  isNewGroup: boolean;
  groupForm: GroupForm;
  previousStatus?: VenueRequestStatus;
  selectedGroup?: GroupBooking;
  selectedSailingBundle?: BareSimpleSailingDayBundle;
  selectedVenue: VenueTemplate;
  showDayContext?: boolean;
  hideDayContextButton?: boolean;
  hideDraft?: boolean;
  refreshing?: boolean;
  submitTitle?: string;
  onGoBack?: () => void;
  onGoForward?: () => void;
  onClose?: () => void;
  onToggleShowDayContext?: () => void;
  onSubmitBookingForm: (status?: VenueRequestStatus) => Promise<void>;
  onUpdateFile: (evt: VenueRequestFile) => void;
  onUploadFile: (file: File) => void;
  onDeleteFile: (file: VenueRequestFile) => void;
}

export default function EnterBookingPage(props: EnterBookingDetailsPageProps) {
  const { activeProperty } = useActiveProperty();
  const { user } = useUserInfo();
  const [isBusy, setIsBusy] = useState(false);

  // important to use a key that stays consistent as this typeahead uses options for the
  // purposes of allowing complex list input (e.g. do not use uuid())
  const addtResources = props.bookingForm.additionalResources.map(t => ({
    key: t.name,
    refKey: t.name,
    item: t,
  }));

  const prepPackages = props.bookingForm.prepaidPackages.map(p => ({
    key: p,
    refKey: p,
    item: p,
  }));

  async function handleOnSubmitGroupBooking(status?: VenueRequestStatus) {
    const statusToUse =
      props.previousStatus === 'approved' ? 'modified' : status || props.previousStatus;

    setIsBusy(true);

    try {
      if (props.onSubmitBookingForm) await props.onSubmitBookingForm(statusToUse);
      if (props.onGoForward) props.onGoForward();
    } finally {
      setIsBusy(false);
    }
  }

  const formValid =
    props.bookingForm.name.length > 0 && props.bookingForm.startTime && props.bookingForm.endTime;

  const meetsTimeBoundary =
    !hasVenueHoursConflict(
      props.selectedVenue.bookingHours,
      props.bookingForm.startTime,
      props.bookingForm.endTime,
      timeToMinutes(activeProperty.dayStart || DEFAULT_DAY_START)
    ) || canBookRequestsBypassingTimeRestrictions(user);

  const canSubmit = !isBusy && formValid && meetsTimeBoundary;

  return (
    <div className="tflf-3">
      <FormGenericContainer
        footer={() => (
          <>
            <FormStatusFooter {...props} className="text-slate-dark mr-auto" />
            {props.onGoBack && (
              <BubbleButton variant="navy" inverse onClick={props.onGoBack} disabled={isBusy}>
                Go Back
              </BubbleButton>
            )}
            {!props.hideDraft && (
              <BubbleButton
                variant="banana"
                className="mr-3"
                disabled={!canSubmit}
                onClick={() => handleOnSubmitGroupBooking('draft')}
              >
                Save as Draft
              </BubbleButton>
            )}
            <BubbleButton
              variant="navy"
              disabled={!canSubmit}
              onClick={() => handleOnSubmitGroupBooking()}
            >
              {props.submitTitle || 'Finish: Submit Group Booking Request'}
            </BubbleButton>
          </>
        )}
      >
        <div className="flex items-start mb-5">
          <div className="mr-auto">
            <div className="flex items-center">
              <h1
                className="text-lg text-slate-dark font-semibold"
                dangerouslySetInnerHTML={{ __html: props.title || 'Create Group Booking' }}
              />
              <BubbleBadge color={hashStringToColor(activeProperty.name)} className="ml-3">
                {activeProperty.name}
              </BubbleBadge>
            </div>
            <h2 className="text-2xl text-navy">
              {props.pageIndex !== undefined ? `${props.pageIndex}. ` : ''}Group Event Details
            </h2>
          </div>
          <div className="ml-auto" />
          <BubbleButton
            variant="navy"
            inverse
            onClick={props.onClose}
            icon="xmark-large"
            iconSize="xl"
          />
        </div>
        <div>
          {props.clearBookingForm && (
            <div className="-my-5 text-right relative">
              <BubbleButton variant="smoke" inverse onClick={props.clearBookingForm} icon="eraser">
                Clear Form
              </BubbleButton>
            </div>
          )}
          <div className="grid grid-cols-3 gap-4 relative">
            <BubbleFormGroup label="Group Event Details" className="col-span-3" asterisk>
              <BubbleInput
                block
                type="text"
                value={props.bookingForm.name}
                onValue={val => props.changeBookingForm('name', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Start Time" className="flex-grow" asterisk>
              <BubbleInput
                block
                type="time"
                value={props.bookingForm.startTime}
                onValue={val => props.changeBookingForm('startTime', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="End Time" className="flex-grow" asterisk>
              <BubbleInput
                block
                type="time"
                value={props.bookingForm.endTime}
                onValue={val => props.changeBookingForm('endTime', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Guests (min. 0)" className="flex-grow">
              <BubbleInput
                block
                type="number"
                min="0"
                value={props.bookingForm.numGuests}
                onValue={val => props.changeBookingForm('numGuests', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Set Up Time (minutes)" className="flex-grow">
              <BubbleInput
                block
                type="number"
                min="0"
                value={props.bookingForm.bufferTime.setUpTime}
                onValue={val =>
                  props.changeBookingForm('bufferTime', {
                    ...props.bookingForm.bufferTime,
                    setUpTime: val,
                  })
                }
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Breakdown Time (minutes)" className="flex-grow">
              <BubbleInput
                block
                type="number"
                min="0"
                value={props.bookingForm.bufferTime.breakdownTime}
                onValue={val =>
                  props.changeBookingForm('bufferTime', {
                    ...props.bookingForm.bufferTime,
                    breakdownTime: val,
                  })
                }
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Food & Beverages" className="col-span-3">
              <BubbleTypeahead
                block
                options={addtResources}
                placeholder="Enter food or beverage name..."
                menuClassName="max-h-80 overflow-y-auto"
                allowNew
                noSuggestionsClick
                alwaysShowSuggestions={addtResources.length > 0}
                suggestionsText="No similar food or beverage names found"
                suggestionClassName="p-1-i pl-4-i"
                suggestionsRenderFn={(label, opt) => (
                  <div className="flex items-center">
                    <span className="mr-auto">{label}</span>
                    <span className="ml-3 text-xs font-semibold uppercase text-navy mr-3 whitespace-nowrap">
                      Amount (min. 0)
                    </span>
                    <BubbleInput
                      type="number"
                      className="flex-shrink-0 max-w-28"
                      appearance="bare"
                      inputClassName="px-3 py-1 rounded"
                      value={opt.item.amount}
                      min={0}
                      onValue={val => {
                        const arIdx = props.bookingForm.additionalResources.findIndex(
                          ar => ar.name === opt.item.name
                        );
                        if (arIdx < 0) return;
                        const newAddRes = [...props.bookingForm.additionalResources];
                        newAddRes.splice(arIdx, 1, { ...opt.item, amount: val });
                        props.changeBookingForm('additionalResources', newAddRes);
                      }}
                    />
                  </div>
                )}
                onCreate={val =>
                  props.changeBookingForm('additionalResources', [
                    ...props.bookingForm.additionalResources,
                    { name: val, amount: 1 },
                  ])
                }
                onTrash={val =>
                  props.changeBookingForm(
                    'additionalResources',
                    props.bookingForm.additionalResources.filter(ar => ar.name !== val.item.name)
                  )
                }
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Entertainment" className="col-span-3">
              <BubbleTypeahead
                block
                options={prepPackages}
                placeholder="Enter entertainment name..."
                menuClassName="max-h-80 overflow-y-auto"
                allowNew
                noSuggestionsClick
                alwaysShowSuggestions={prepPackages.length > 0}
                suggestionsText="No similar entertainment names found"
                onCreate={val =>
                  props.changeBookingForm('prepaidPackages', [
                    ...props.bookingForm.prepaidPackages,
                    val,
                  ])
                }
                onTrash={val =>
                  props.changeBookingForm(
                    'prepaidPackages',
                    props.bookingForm.prepaidPackages.filter(pp => pp !== val.item)
                  )
                }
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Housekeeping Requests" className="col-span-3">
              <BubbleInput
                block
                type="text"
                value={props.bookingForm.dining}
                onValue={val => props.changeBookingForm('dining', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Venue Configuration" className="col-span-3">
              <BubbleSelect
                block
                allowUndefined
                labelExtractor={c => `${c.name} (occ. ${c.occupancy})`}
                options={props.venueConfigOpts}
                value={props.bookingForm.venueConfiguration}
                onValue={val => props.changeBookingForm('venueConfiguration', val)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup label="Group Event Notes" className="col-span-3">
              <TinyEditor
                value={props.bookingForm.notes}
                onValue={val => props.changeBookingForm('notes', val)}
                mode="text"
              />
            </BubbleFormGroup>
            <div className="col-span-3">
              <BubbleFileM
                label="Group Event Files"
                files={props.bookingForm.files}
                onUpdateFile={props.onUpdateFile}
                onUploadFile={props.onUploadFile}
                onDeleteFile={props.onDeleteFile}
              />
            </div>
            <div className="col-span-3">
              {!meetsTimeBoundary && (
                <BubbleAlert variant="banana">
                  To submit this booking, its time must be within open bookings hours as specified
                  on the venue.
                </BubbleAlert>
              )}
            </div>
          </div>
        </div>
      </FormGenericContainer>
    </div>
  );
}
